// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import { extractStylesToArray, isSafe } from "@insite/client-framework/Common/Utilities/isSafeStyles";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@sc/App/Components/Notify";
import { XNGAGE_CUSTOM_WIDGET } from "@sc/XDL/constants";
import parse from "html-react-parser";
import * as React from "react";
import { FC } from "react";

const enum fields {
    content = "content",
    type = "type",
    showClose = "showClose",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.content]: string;
        [fields.type]: "success" | "danger" | "warning" | "info";
        [fields.showClose]: boolean;
    };
}

type Props = OwnProps;
export interface RichContentStyles {
    wrapper?: InjectableCss;
}

const NotifyWidget: FC<Props> = ({ fields }) => {
    if (!fields.content) {
        return null;
    }

    return (
        <Notify type={fields.type} showClose={fields.showClose} fullWidth={true}>
            {parse(fields.content, parserOptions)}
        </Notify>
    );
};

const widgetModule: WidgetModule = {
    component: NotifyWidget,
    definition: {
        group: XNGAGE_CUSTOM_WIDGET,
        displayName: "Notify",
        icon: "text",
        fieldDefinitions: [
            {
                name: fields.content,
                displayName: "Content",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Contextual",
                validate: (html: string) => {
                    const arrayOfStyles = extractStylesToArray(html);
                    return arrayOfStyles?.every(style => isSafe(style))
                        ? null
                        : "The HTML contains invalid styles that prevent it from being saved";
                },
            },
            {
                name: fields.type,
                displayName: "Type",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "Danger",
                        value: "danger",
                    },
                    {
                        displayName: "Info",
                        value: "info",
                    },
                    {
                        displayName: "Success",
                        value: "success",
                    },
                    {
                        displayName: "Warning",
                        value: "warning",
                    },
                ],
                isRequired: false,
                defaultValue: "info",
                fieldType: "General",
            },
            {
                name: fields.showClose,
                displayName: "Show Close",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
